import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { base_url } from "../utils/axiosConfig";
import axios from 'axios';
import { FaArrowLeft, FaEnvelope } from "react-icons/fa";
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../images/HeadLogo.png';

const ForgetPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${base_url}user/forgot-password-token`, { email });
      setMessage('Password reset link sent to your email.');
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset link. Please try again.');
      setMessage('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-vh-100 d-flex justify-content-center align-items-center p-3"
      style={{
        background: 'linear-gradient(135deg, #f0f9f0 0%, #e8f5e9 100%)',
        fontFamily: "'Poppins', sans-serif"
      }}
    >
      <motion.div 
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-100"
        style={{ maxWidth: '380px' }}
      >
        <div 
          className="card border-0 shadow-lg"
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 15px 50px rgba(45, 90, 39, 0.1)'
          }}
        >
          <div className="card-body p-4">
            {/* Simple Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="btn border-0 d-flex align-items-center gap-2 text-secondary mb-3 p-0 auth-back-btn"
              style={{ fontSize: '13px', transition: '0.2s' }}
            >
              <FaArrowLeft size={12} />
              <span>Back</span>
            </button>

            <div className="text-center mb-4">
              <motion.img 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                src={logo} 
                alt="Logo" 
                className="mb-2" 
                style={{ height: '48px', objectFit: 'contain' }} 
              />
              <h4 className="fw-bold mb-1" style={{ color: '#1b5e20', letterSpacing: '-0.3px' }}>Reset Password</h4>
              <p className="text-muted small mb-0" style={{ fontSize: '13px' }}>Enter your email to receive instructions</p>
            </div>

            <AnimatePresence>
              {message && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  className="alert alert-success border-0 small text-center mb-3"
                  style={{ borderRadius: '10px', fontSize: '12px' }}
                >
                  {message}
                </motion.div>
              )}

              {error && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  className="alert alert-danger border-0 small text-center mb-3"
                  style={{ borderRadius: '10px', fontSize: '12px' }}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
              <div className="form-group">
                <label className="form-label small fw-bold text-dark mb-1 ms-1" style={{ fontSize: '12px' }}>Email Address</label>
                <div className="input-group premium-input-group">
                  <span className="input-group-text bg-white border-end-0 text-secondary ps-3" style={{ padding: '8px' }}>
                    <FaEnvelope size={14} />
                  </span>
                  <input
                    className="form-control border-start-0 ps-2 py-2"
                    style={{ fontSize: '14px', borderRadius: '0 10px 10px 0', height: '45px' }}
                    type="email"
                    placeholder="name@example.com"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-success w-100 py-2 mt-1 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
                style={{ 
                  backgroundColor: '#3b7d2f',
                  borderColor: '#3b7d2f',
                  borderRadius: '12px',
                  fontSize: '15px',
                  height: '48px',
                  transition: 'all 0.3s ease'
                }}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  'Send Instructions'
                )}
              </button>

              <div className="text-center mt-2">
                <Link to="/login" className="text-decoration-none small fw-bold text-success" style={{ fontSize: '13px' }}>
                  Back to Login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </motion.div>

      <style>{`
        .premium-input-group .form-control {
          border-color: #e0e0e0;
          background-color: #fcfdfc;
          transition: all 0.2s ease;
        }
        .premium-input-group .input-group-text {
          border-color: #e0e0e0;
          background-color: #fcfdfc;
        }
        .premium-input-group:focus-within .form-control,
        .premium-input-group:focus-within .input-group-text {
          border-color: #3b7d2f !important;
          background-color: #fff !important;
          box-shadow: none;
        }
        .premium-input-group:focus-within {
          box-shadow: 0 0 0 3px rgba(59, 125, 47, 0.1);
          border-radius: 10px;
        }
        .form-control:focus {
          box-shadow: none !important;
        }
        .btn-success:hover {
          background-color: #2d5a27 !important;
          transform: translateY(-1px);
          box-shadow: 0 5px 15px rgba(59, 125, 47, 0.25) !important;
        }
        .btn-success:active {
          transform: translateY(0);
        }
        .auth-back-btn:hover {
          color: #1b5e20 !important;
          transform: translateX(-2px);
        }
        @media (max-width: 576px) {
          .card-body {
            padding: 24px 20px !important;
          }
        }
      `}</style>
    </div>
  );

};

export default ForgetPassword;